<template>
    <LoadingComponent :props="loading" />
    <div class="col-12">
        <div class="db-card p-4">
            <div class="flex flex-col items-start justify-start">
                <div class="flex flex-wrap justify-start items-center border-b w-full gap-y-2 gap-x-6 pb-3 mb-4">
                    <p class="text-2xl font-medium font-client mt-0.5">
                        {{ $t('label.order') }}
                        <span class="text-primary">
                            #{{ order.order_serial_no }}
                        </span>
                    </p>
                    <div class="flex items-center gap-2">
                        <span :class="'db-table-badge px-2 rounded-3xl ' + statusClass(order.payment_status)">
                            {{ $t('label.payment_status') }}: {{ enums.paymentStatusEnumArray[order.payment_status] }}
                        </span>
                        <span :class="'db-table-badge px-2 rounded-3xl ' + orderStatusClass(order.status)">
                            {{ $t('label.order_status') }}: {{ enums.orderStatusEnumArray[order.status] }}
                        </span>
                    </div>
                </div>
                <div class="w-full flex flex-wrap items-center justify-between gap-4">
                    <div class="md:w-auto">
                        <ul class="flex items-center gap-2">
                            <li class="flex items-center gap-2 py-2 pr-2 border-r">
                                <i class="lab lab-line-calendar lab-font-size-16"></i>
                                <span class="text-xs">{{ order.order_datetime }}</span>
                            </li>
                            <li class="text-xs py-2 pr-2 border-r">
                                {{ $t('label.payment_type') }}:
                                <span class="text-heading">
                                    {{ order.pos_payment_method_name }}
                                </span>
                            </li>
                            <li v-if="order.pos_payment_method == enums.posPaymentMethodEnum.CARD" class="text-xs py-2 pr-2">
                                {{ $t('label.card_no') }}:
                                <span class="text-heading">
                                    {{ order.pos_payment_note }}
                                </span>
                            </li>
                            <li v-if="order.pos_payment_method == enums.posPaymentMethodEnum.MOBILE_BANKING" class="text-xs py-2 pr-2 border-r">
                                {{ $t('label.transaction_id') }}:
                                <span class="text-heading">
                                    {{ order.pos_payment_note }}
                                </span>
                            </li>
                            <li v-if="order.pos_payment_method == enums.posPaymentMethodEnum.OTHER" class="text-xs py-2 pr-2 border-r">
                                {{ $t('label.note') }}:
                                <span class="text-heading">
                                    {{ order.pos_payment_note }}
                                </span>
                            </li>
                            <li class="text-xs py-2 pr-2">
                                {{ $t('label.order_type') }}:
                                <span class="text-heading">
                                    {{ $t("label.pos") }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="flex flex-nowrap justify-start items-center gap-3">
                        <vue-select
                            class="db-field-control f-b-custom-select !w-150"
                            v-model="payment_status"
                            :options="enums.paymentStatusObject"
                            label-by="name"
                            value-by="value"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                            @update:modelValue="changePaymentStatus"
                        />

                        <vue-select
                            class="db-field-control f-b-custom-select !w-150"
                            v-model="order_status"
                            :options="enums.orderStatusObject"
                            label-by="name"
                            value-by="value"
                            :closeOnSelect="true"
                            :searchable="false"
                            :clearOnClose="true"
                            @update:modelValue="orderStatus"
                        />

                        <button type="button" v-print="printObj"
                            class="flex items-center justify-center gap-2 px-3 h-[38px] rounded-md bg-primary hover:opacity-80">
                            <i class="lab lab-line-printer lab-font-size-16 text-white"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="col-12 sm:col-6">
        <div class="db-card p-1">
            <div class="db-card-header py-3">
                <h3 class="db-card-title">{{ $t('label.order_items') }}</h3>
            </div>
            <div class="db-card-body">
                <div class="pl-3">
                    <div class="mb-3 pb-3 border-b last:mb-0 last:pb-0 last:border-b-0 border-gray-2"
                        v-if="orderProducts.length > 0" v-for="product in orderProducts" :key="product">
                        <div class="flex items-center gap-3 relative">
                            <h3
                                class="absolute top-5 ltr:-left-3 rtl:-right-3  text-sm w-[26px] h-[26px] leading-[26px] text-center rounded-full text-white bg-heading">
                                {{ product.quantity }}
                            </h3>
                            <img class="w-16 h-16 rounded-lg flex-shrink-0" :src="product.product_image"
                                alt="thumbnail">
                            <div class="flex flex-col justify-center items-start overflow-hidden">
                                <h4 :class="!product.variation_names ? '' : ''"
                                    class="text-sm capitalize whitespace-nowrap overflow-hidden text-ellipsis">
                                    {{ product.product_name }}
                                </h4>
                                <p class="text-sm overflow-hidden">{{ product.variation_names }}</p>
                                <div class="flex flex-wrap items-center justify-between gap-4">
                                    <div class="flex items-center gap-4">
                                        <span class="text-sm font-semibold">
                                            {{ product.subtotal_currency_price }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="col-12 sm:col-6">
        <div class="row">
            <div class="col-12">
                <div class="db-card p-1">
                    <div class="db-card-header py-3">
                        <h3 class="db-card-title">{{ $t('label.order_resume') }}</h3>
                    </div>
                    <div class="db-card-body px-2 py-1">
                        <ul class="flex flex-col gap-2 p-3 border-b border-dashed">
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.subtotal') }}</span>
                                <span class="text-sm font-medium">{{ order.subtotal_currency_price }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.tax_fee') }}</span>
                                <span class="text-sm font-medium">{{ order.tax_currency_price }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                                <span class="text-sm font-medium">{{ $t('label.discount') }}</span>
                                <span class="text-sm font-medium">{{ order.discount_currency_price }}</span>
                            </li>
                        </ul>
                        <div class="flex items-center justify-between p-3">
                            <h4 class="text-sm font-medium">{{ $t('label.total') }}</h4>
                            <h5 class="text-sm font-medium">
                                {{ order.total_currency_price }}
                            </h5>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12">
                <div class="db-card p-1">
                    <div class="db-card-header py-3">
                        <h3 class="db-card-title">{{ $t('label.information') }}</h3>
                    </div>
                    <div class="db-card-body">
                        <div class="flex items-center gap-3 mb-4">
                            <img class="w-8 rounded-full" :src="orderUser.image" alt="avatar">
                            <h4 class="font-semibold text-sm capitalize">
                                {{ textShortener(orderUser.name, 20) }}
                            </h4>
                        </div>
                        <ul class="flex flex-col gap-3 pt-4 border-t">
                            <li class="flex items-center gap-2.5">
                                <i class="lab lab-line-mail lab-font-size-14"></i>
                                <span class="text-xs">{{ orderUser.email }}</span>
                            </li>
                            <li class="flex items-center gap-2.5">
                                <i class="lab lab-line-call-calling lab-font-size-14"></i>
                                <span class="text-xs" dir="ltr">{{ orderUser.country_code + '' + orderUser.phone
                                    }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <PosOrderReceiptComponent :order="order" />
</template>
<script>
import LoadingComponent from "../components/LoadingComponent";
import alertService from "../../../services/alertService";
import PaginationTextComponent from "../components/pagination/PaginationTextComponent";
import PaginationBox from "../components/pagination/PaginationBox";
import PaginationSMBox from "../components/pagination/PaginationSMBox";
import appService from "../../../services/appService";
import orderStatusEnum from "../../../enums/modules/orderStatusEnum";
import TableLimitComponent from "../components/TableLimitComponent";
import paymentStatusEnum from "../../../enums/modules/paymentStatusEnum";
import print from "vue3-print-nb";
import PosOrderReceiptComponent from "./PosOrderReceiptComponent";
import posPaymentMethodEnum from "../../../enums/modules/posPaymentMethodEnum";

export default {
    name: "PosOrderShowComponent",
    components: {
        TableLimitComponent,
        PaginationSMBox,
        PaginationBox,
        PaginationTextComponent,
        LoadingComponent,
        PosOrderReceiptComponent
    },
    directives: {
        print
    },
    data() {
        return {
            loading: {
                isActive: false
            },
            printLoading: true,
            printObj: {
                id: "print",
                popTitle: this.$t("menu.pos_order_receipt"),
            },
            enums: {
                orderStatusEnum: orderStatusEnum,
                paymentStatusEnum: paymentStatusEnum,
                posPaymentMethodEnum: posPaymentMethodEnum,
                orderStatusEnumArray: {
                    [orderStatusEnum.CONFIRMED]: this.$t("label.confirmed"),
                    [orderStatusEnum.DELIVERED]: this.$t("label.delivered"),
                },
                paymentStatusEnumArray: {
                    [paymentStatusEnum.PAID]: this.$t("label.paid"),
                    [paymentStatusEnum.UNPAID]: this.$t("label.unpaid")
                },
                paymentStatusObject: [
                    {
                        name: this.$t("label.paid"),
                        value: paymentStatusEnum.PAID
                    },
                    {
                        name: this.$t("label.unpaid"),
                        value: paymentStatusEnum.UNPAID,
                    },
                ],
                orderStatusObject: [
                    {
                        name: this.$t("label.confirmed"),
                        value: orderStatusEnum.CONFIRMED,
                    },
                    {
                        name: this.$t("label.delivered"),
                        value: orderStatusEnum.DELIVERED,
                    },
                ],
            },
            payment_status: null,
            order_status: null
        }
    },
    mounted() {
        this.loading.isActive = true;
        this.$store.dispatch('posOrder/show', this.$route.params.id).then(res => {
            this.payment_status = res.data.data.payment_status;
            this.order_status = res.data.data.status;
            this.loading.isActive = false;
        }).catch((error) => {
            this.loading.isActive = false;
        });
    },
    computed: {
        order: function () {
            return this.$store.getters['posOrder/show'];
        },
        orderProducts: function () {
            return this.$store.getters['posOrder/orderProducts'];
        },
        orderUser: function () {
            return this.$store.getters['posOrder/orderUser'];
        }
    },
    methods: {
        statusClass: function (status) {
            return appService.statusClass(status);
        },
        orderStatusClass: function (status) {
            return appService.orderStatusClass(status);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        orderStatus: function (e) {
            const status = e?.target?.value ?? e;
            try {
                this.loading.isActive = true;
                this.$store.dispatch("posOrder/changeStatus", {
                    id: this.$route.params.id,
                    status: status,
                }).then((res) => {
                    this.loading.isActive = false;
                    alertService.successFlip(
                        1,
                        this.$t("label.status")
                    );
                }).catch((err) => {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            }
        },
        changePaymentStatus: function (e) {
            const paymentStatus = e?.target?.value ?? e;
            try {
                this.loading.isActive = true;
                this.$store.dispatch("posOrder/changePaymentStatus", {
                    id: this.$route.params.id,
                    payment_status: paymentStatus,
                }).then((res) => {
                    this.loading.isActive = false;
                    alertService.successFlip(
                        1,
                        this.$t("label.payment_status")
                    );
                }).catch((err) => {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                });
            } catch (err) {
                this.loading.isActive = false;
                alertService.error(err.response.data.message);
            }
        },
    },
}
</script>